import {groupBy, objectFromEntries} from '@benchling/js-utils';
import assert from 'assert';
import isEqual from 'react-fast-compare';
import {Connection} from 'reactflow';

import {AnalysisGraphEdgeData} from 'analysis-tool/flowchart/analysis-graph/AnalysisGraphTypes';
import AnalysisGraphUtils from 'analysis-tool/flowchart/analysis-graph/AnalysisGraphUtils';
import * as PipelineDAO from 'pipelines/network/PipelineDAO';
import {PipelineEdgeUpsertById} from 'pipelines/network/PipelineDAO';
import {PipelineEdge, PipelineGraphQL, PipelineStep} from 'pipelines/PipelineTypes';
import * as logging from 'util/Logging';
import removeNulls from 'util/removeNulls';

/**
 * Convert an edge to an upserts, so it can be re-upserted without modifications.
 */
function convertEdgeToUpsert(
  edge: Pick<
    PipelineEdge,
    'fromStep' | 'fromStepData' | 'fromStepDataIndex' | 'toStepData' | 'toStepDataIndex' | 'condition'
  >
): PipelineEdgeUpsertById {
  return {
    fromStepId: edge.fromStep.id,
    fromStepDataName: edge.fromStepData.name,
    fromStepDataIndex: edge.fromStepDataIndex,
    toStepDataName: edge.toStepData.name,
    toStepDataIndex: edge.toStepDataIndex,
    condition: edge.condition,
  };
}
/**
 * Convert a step's incoming edges to upserts, so they can be re-upserted without modifications.
 */
function convertIncomingEdgesToUpserts(
  step: Pick<PipelineStep, 'incomingEdges'>
): Array<PipelineEdgeUpsertById> {
  return step.incomingEdges.map((edge) => convertEdgeToUpsert(edge));
}

function translateConnectionToEdgeToCreate(
  pipeline: Pick<PipelineGraphQL, 'steps' | 'id'>,
  connection: Connection
): Omit<AnalysisGraphEdgeData['edge'], '__typename'> | null {
  const fromStep = pipeline.steps.find((step) => AnalysisGraphUtils.getNodeId(step) === connection.source);
  const toStep = pipeline.steps.find((step) => AnalysisGraphUtils.getNodeId(step) === connection.target);

  const fromStepData = fromStep?.outputStepDatas.find(
    (stepData) => AnalysisGraphUtils.getHandleId(stepData) === connection.sourceHandle
  );
  const toStepData = toStep?.inputStepDatas.find(
    (stepData) => AnalysisGraphUtils.getHandleId(stepData) === connection.targetHandle
  );

  if (fromStep == null || toStep == null || fromStepData == null || toStepData == null) {
    logging.warn('Tried to connect steps that do not exist');
    return null;
  }

  return {
    fromStep,
    fromStepData,
    fromStepDataIndex: null,
    toStep,
    toStepData,
    toStepDataIndex: null,
    condition: null,
  };
}

/**
 * Remove edge upserts which may send the same raw data.
 *
 * While multiple edges may connect two steps, those edges cannot send the same raw data to the same input
 * step data; this would break the assumption that a step data does not contain the same raw data multiple
 * times.
 */
function deduplicateIncomingEdges(edgeUpserts: Array<PipelineEdgeUpsertById>): Array<PipelineEdgeUpsertById> {
  assert(new Set(edgeUpserts.map(({toStepDataName}) => toStepDataName)).size <= 1);

  return edgeUpserts.filter((edge, index) =>
    edgeUpserts.slice(0, index).every(
      // Check against preceding steps
      (otherEdge) =>
        edge.fromStepId !== otherEdge.fromStepId ||
        edge.fromStepDataName !== otherEdge.fromStepDataName ||
        (edge.fromStepDataIndex ?? otherEdge.fromStepDataIndex) !==
          (otherEdge.fromStepDataIndex ?? edge.fromStepDataIndex)
    )
  );
}

/**
 * Adjust incoming edges to reconcile subtle conflicts, including:
 *  • Duplicate edges, which send the same raw data to the same input step data
 *  • Non-sequential or overlapping values for `toStepDataIndex`
 *  • A mix of null and non-null values for `toStepDataIndex`
 */
function resolveConflictsInIncomingEdges(
  edgeUpserts: Array<PipelineEdgeUpsertById>
): Array<PipelineEdgeUpsertById> {
  return Object.values(groupBy(edgeUpserts, ({toStepDataName}) => toStepDataName)).flatMap((edges) => {
    const deduplicatedEdges = deduplicateIncomingEdges(edges);

    // If `toStepDataIndex` is null for all edges, there are no conflicts; the backend will infer appropriate
    // indices, as necessary.
    if (deduplicatedEdges.every(({toStepDataIndex}) => toStepDataIndex == null)) {
      return deduplicatedEdges;
    }

    // At least one `toStepDataIndex` is a specific value; to close any gaps or overlaps in `toStepDataIndex`,
    // sort edges by their existing `toStepDataIndex`, then re-infer new indices, in order. Sort nulls last.
    return [...deduplicatedEdges]
      .sort(({toStepDataIndex: a}, {toStepDataIndex: b}) => (a ?? 1_000_000) - (b ?? 1_000_000))
      .map((edgeUpsert, index) => ({...edgeUpsert, toStepDataIndex: index}));
  });
}

export async function connectSteps(
  pipeline: Pick<PipelineGraphQL, 'steps' | 'id'>,
  edgeToReplace: AnalysisGraphEdgeData['edge'] | null,
  connection: Connection | null
): Promise<void> {
  const edgeToCreate = connection && translateConnectionToEdgeToCreate(pipeline, connection);

  // There is nothing to change
  if (edgeToReplace == null && edgeToCreate == null) {
    return;
  }

  // Convert incoming edges on the steps to modify to upserts. The edge to remove and the edge to create may
  // have the same, or different, target steps.
  const incomingEdgesByStepId = objectFromEntries(
    removeNulls([edgeToReplace, edgeToCreate]).map(({toStep}) => [
      toStep.id,
      convertIncomingEdgesToUpserts(toStep),
    ])
  );

  // If an edge is being replaced or removed, remove its upsert.
  if (edgeToReplace) {
    incomingEdgesByStepId[edgeToReplace.toStep.id] = incomingEdgesByStepId[edgeToReplace.toStep.id].filter(
      (edgeUpsert) => !isEqual(edgeUpsert, convertEdgeToUpsert(edgeToReplace))
    );
  }

  // If an edge is being added, insert a new upsert.
  if (edgeToCreate) {
    incomingEdgesByStepId[edgeToCreate.toStep.id] = [
      // Remove `toStepDataIndex`, so the backend re-infers a valid sequence of raw data indices.
      ...incomingEdgesByStepId[edgeToCreate.toStep.id],
      convertEdgeToUpsert({...edgeToCreate, fromStepDataIndex: null, toStepDataIndex: null}),
    ];
  }

  await PipelineDAO.addAndUpdatePipelineSteps(pipeline.id, {
    stepsToAdd: [],
    stepsToUpdate: Object.entries(incomingEdgesByStepId).map(([stepId, incomingEdges]) => ({
      stepId,
      incomingEdges: resolveConflictsInIncomingEdges(incomingEdges),
      rerunAfterUpdate: true,
    })),
  });
}
